import React, { useEffect } from 'react';
import { X } from 'lucide-react';
export type LegalType = 'privacy' | 'terms';
interface LegalModalProps {
  type: LegalType;
  onClose: () => void;
}
const content = {
  privacy: {
    title: 'Política de Privacidad',
    body: [{
      heading: 'Descripción general',
      text: 'Esta Política de Privacidad describe cómo se maneja la información en relación con la compra y descarga del ebook digital "El Azúcar Silencioso".'
    }, {
      heading: 'Información que recopilamos',
      text: 'No recopilamos información personal directamente a través de esta página. Cualquier dato personal (nombre, correo electrónico, datos de pago) es recopilado y procesado exclusivamente por el procesador de pagos (por ejemplo, Shopify, Stripe) al momento de la compra. No almacenamos información de pago.'
    }, {
      heading: 'Uso de la información',
      text: 'Tu dirección de correo electrónico puede usarse únicamente para entregar el enlace de descarga del producto adquirido. No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.'
    }, {
      heading: 'Cookies',
      text: 'Esta página puede usar almacenamiento mínimo del navegador (localStorage). No se utilizan cookies de seguimiento ni píxeles publicitarios en esta página.'
    }, {
      heading: 'Entrega del producto digital',
      text: 'Después de la compra, se enviará un enlace de descarga al correo electrónico proporcionado en el proceso de pago. No somos responsables de fallas en la entrega causadas por direcciones de correo incorrectas o filtros de spam.'
    }, {
      heading: 'Cambios en esta política',
      text: 'Nos reservamos el derecho de actualizar esta Política de Privacidad en cualquier momento. El uso continuo de esta página constituye la aceptación de cualquier cambio.'
    }, {
      heading: 'Aviso educativo',
      text: 'Este producto es solo con fines educativos. No se recopilan ni procesan datos médicos, de salud ni personales a través de esta página.'
    }]
  },
  terms: {
    title: 'Términos de Uso',
    body: [{
      heading: 'Aceptación de los términos',
      text: 'Al comprar y descargar el ebook "El Azúcar Silencioso", aceptas estos Términos de Uso. Si no estás de acuerdo, por favor no completes la compra.'
    }, {
      heading: 'Producto digital — Sin reembolsos',
      text: 'Este es un producto digital entregado mediante descarga inmediata. Debido a la naturaleza de los bienes digitales, todas las ventas son finales. No se emitirán reembolsos una vez que se haya accedido al enlace de descarga.'
    }, {
      heading: 'Solo uso educativo',
      text: 'El contenido de este ebook se proporciona únicamente con fines educativos e informativos. No constituye consejo médico, diagnóstico ni tratamiento. Consulta siempre a un profesional de salud calificado antes de realizar cambios en tu dieta, estilo de vida o rutina de salud.'
    }, {
      heading: 'Propiedad intelectual',
      text: 'Todo el contenido de este ebook, incluyendo texto, gráficos y diseño, es propiedad intelectual del autor. No puedes reproducir, distribuir, revender ni compartir el contenido sin permiso escrito explícito.'
    }, {
      heading: 'Licencia de uso personal',
      text: 'Tu compra te otorga una licencia de usuario único, no transferible, para leer y usar este ebook únicamente para fines personales y no comerciales.'
    }, {
      heading: 'Limitación de responsabilidad',
      text: 'El autor y el editor no son responsables de ningún resultado derivado de la aplicación de la información contenida en este ebook. El uso de este material es bajo tu propio riesgo.'
    }, {
      heading: 'Ley aplicable',
      text: 'Estos términos se rigen por las leyes aplicables. Cualquier disputa se resolverá de acuerdo con las leyes de la jurisdicción en la que se vende el producto.'
    }]
  }
};
export function LegalModal({
  type,
  onClose
}: LegalModalProps) {
  const t = content[type];
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-75">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-76" />

      <div className="relative z-10 w-full max-w-2xl max-h-[85vh] bg-[#141414] border border-[#2a2a2a] rounded-2xl shadow-[0_0_60px_rgba(0,0,0,0.8)] flex flex-col" data-id="element-77">
        <div className="flex items-center justify-between px-8 py-6 border-b border-[#2a2a2a] shrink-0" data-id="element-78">
          <h2 id="legal-modal-title" className="text-2xl font-serif text-[#C9A84C]" data-id="element-79">
            {t.title}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] text-[#888] hover:text-white transition-colors" aria-label="Cerrar" data-id="element-80">
            <X className="w-4 h-4" data-id="element-81" />
          </button>
        </div>

        <div className="overflow-y-auto px-8 py-6 space-y-6" data-id="element-82">
          {t.body.map((section, idx) => <div key={idx} data-id="element-83">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-[#C9A84C] mb-2" data-id="element-84">
                {section.heading}
              </h3>
              <p className="text-[#9a9a9a] text-sm leading-relaxed" data-id="element-85">
                {section.text}
              </p>
            </div>)}
          <div className="pt-4 border-t border-[#2a2a2a]" data-id="element-86">
            <p className="text-xs text-[#444]" data-id="element-87">Última actualización: 2025</p>
          </div>
        </div>
      </div>
    </div>;
}